'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

type ItemStatus = 'EXPIRED' | 'EXPIRING_SOON' | 'INSPECTION_DUE' | 'ALL_OK';

interface InspectionRecord {
  date: string;
  inspector: string;
  result: 'PASS' | 'FAIL' | 'CONDITIONAL';
  findings: string;
  nextDue: string;
  certificate?: string;
}

interface QHSEItem {
  id: string;
  name: string;
  batch: string;
  site: string;
  category: string;
  standard: string;
  expiryDate: string;
  daysToExpiry: number;
  nextInspection: string;
  daysToInspection: number;
  status: ItemStatus;
  quantity: number;
  unit: string;
  certNumber: string;
  inspectionHistory: InspectionRecord[];
}

const ALL_ITEMS: QHSEItem[] = [
  {
    id: 'Q001',
    name: 'Full Body Safety Harness',
    batch: 'SH-2024-07',
    site: 'Site Alpha',
    category: 'PPE',
    standard: 'EN 361',
    expiryDate: '27 Jul 2026',
    daysToExpiry: -2,
    nextInspection: '27 Jul 2026',
    daysToInspection: -2,
    status: 'EXPIRED',
    quantity: 6,
    unit: 'PCS',
    certNumber: 'CERT-SH-2024-07-A',
    inspectionHistory: [
      { date: '15 Jan 2026', inspector: 'Priya Sharma', result: 'PASS', findings: 'All straps intact, buckles functional, no visible wear.', nextDue: '15 Jul 2026', certificate: 'INS-SH-0115' },
      { date: '10 Jul 2025', inspector: 'Priya Sharma', result: 'PASS', findings: 'Minor abrasion on shoulder strap noted. Acceptable within tolerance.', nextDue: '10 Jan 2026', certificate: 'INS-SH-0710' },
      { date: '05 Jan 2025', inspector: 'Ravi Nair', result: 'CONDITIONAL', findings: 'Dorsal D-ring shows surface rust. Cleaned and treated. Re-inspect in 6 months.', nextDue: '05 Jul 2025' },
    ],
  },
  {
    id: 'Q002',
    name: 'Fire Extinguisher (CO₂ 5kg)',
    batch: 'FE-2026-03',
    site: 'Site Beta',
    category: 'Fire Safety',
    standard: 'EN 3-7',
    expiryDate: '02 Aug 2026',
    daysToExpiry: 4,
    nextInspection: '01 Aug 2026',
    daysToInspection: 3,
    status: 'EXPIRING_SOON',
    quantity: 4,
    unit: 'PCS',
    certNumber: 'CERT-FE-2026-03-B',
    inspectionHistory: [
      { date: '01 Feb 2026', inspector: 'Priya Sharma', result: 'PASS', findings: 'Pressure gauge in green zone. Pin and seal intact. No corrosion.', nextDue: '01 Aug 2026', certificate: 'INS-FE-0201' },
      { date: '01 Aug 2025', inspector: 'Ravi Nair', result: 'PASS', findings: 'All checks passed. Weight within tolerance.', nextDue: '01 Feb 2026', certificate: 'INS-FE-0801' },
    ],
  },
  {
    id: 'Q003',
    name: 'Gas Detector (H₂S)',
    batch: 'GD-2025-12',
    site: 'Site Gamma',
    category: 'Detection Equipment',
    standard: 'IEC 60079-29-1',
    expiryDate: '12 Aug 2026',
    daysToExpiry: 14,
    nextInspection: '01 Aug 2026',
    daysToInspection: 3,
    status: 'INSPECTION_DUE',
    quantity: 3,
    unit: 'PCS',
    certNumber: 'CERT-GD-2025-12-C',
    inspectionHistory: [
      { date: '01 Feb 2026', inspector: 'Priya Sharma', result: 'PASS', findings: 'Sensor response within calibration range. Battery at 87%.', nextDue: '01 Aug 2026', certificate: 'INS-GD-0201' },
      { date: '01 Aug 2025', inspector: 'Priya Sharma', result: 'FAIL', findings: 'H₂S sensor drift detected. Sensor replaced. Recalibrated successfully.', nextDue: '01 Feb 2026' },
      { date: '01 Feb 2025', inspector: 'Ravi Nair', result: 'PASS', findings: 'Full calibration check passed. All alarm thresholds verified.', nextDue: '01 Aug 2025', certificate: 'INS-GD-0202' },
    ],
  },
  {
    id: 'Q004',
    name: 'First Aid Kit (Type B)',
    batch: 'FAK-2026-01',
    site: 'Warehouse B',
    category: 'Medical',
    standard: 'DIN 13169',
    expiryDate: '08 Aug 2026',
    daysToExpiry: 9,
    nextInspection: '08 Aug 2026',
    daysToInspection: 9,
    status: 'EXPIRING_SOON',
    quantity: 2,
    unit: 'KIT',
    certNumber: 'CERT-FAK-2026-01-D',
    inspectionHistory: [
      { date: '08 Feb 2026', inspector: 'Priya Sharma', result: 'PASS', findings: 'All consumables within expiry. Kit fully stocked per DIN 13169 checklist.', nextDue: '08 Aug 2026', certificate: 'INS-FAK-0208' },
    ],
  },
  {
    id: 'Q005',
    name: 'Hard Hat EN 397',
    batch: 'HH-2026-02',
    site: 'Site Alpha',
    category: 'PPE',
    standard: 'EN 397',
    expiryDate: '19 Aug 2026',
    daysToExpiry: 21,
    nextInspection: '19 Aug 2026',
    daysToInspection: 21,
    status: 'EXPIRING_SOON',
    quantity: 12,
    unit: 'PCS',
    certNumber: 'CERT-HH-2026-02-E',
    inspectionHistory: [
      { date: '19 Feb 2026', inspector: 'Ravi Nair', result: 'PASS', findings: 'No cracks, dents or deformation. Harness suspension intact.', nextDue: '19 Aug 2026', certificate: 'INS-HH-0219' },
      { date: '19 Aug 2025', inspector: 'Priya Sharma', result: 'PASS', findings: 'Visual inspection passed. UV degradation test within limits.', nextDue: '19 Feb 2026', certificate: 'INS-HH-0819' },
    ],
  },
  {
    id: 'Q006',
    name: 'Respiratory Mask (FFP3)',
    batch: 'RM-2025-11',
    site: 'Site Gamma',
    category: 'PPE',
    standard: 'EN 149',
    expiryDate: '30 Jul 2026',
    daysToExpiry: -1,
    nextInspection: '30 Jul 2026',
    daysToInspection: -1,
    status: 'EXPIRED',
    quantity: 20,
    unit: 'PCS',
    certNumber: 'CERT-RM-2025-11-F',
    inspectionHistory: [
      { date: '30 Jan 2026', inspector: 'Priya Sharma', result: 'PASS', findings: 'Seal integrity test passed. Filter media within service life.', nextDue: '30 Jul 2026', certificate: 'INS-RM-0130' },
    ],
  },
  {
    id: 'Q007',
    name: 'Fall Arrest Lanyard',
    batch: 'FAL-2025-09',
    site: 'Site Gamma',
    category: 'PPE',
    standard: 'EN 354',
    expiryDate: '15 Sep 2026',
    daysToExpiry: 47,
    nextInspection: '01 Aug 2026',
    daysToInspection: 3,
    status: 'INSPECTION_DUE',
    quantity: 8,
    unit: 'PCS',
    certNumber: 'CERT-FAL-2025-09-G',
    inspectionHistory: [
      { date: '01 Feb 2026', inspector: 'Ravi Nair', result: 'PASS', findings: 'Energy absorber intact. Snap hooks operate correctly.', nextDue: '01 Aug 2026', certificate: 'INS-FAL-0201' },
      { date: '01 Aug 2025', inspector: 'Priya Sharma', result: 'PASS', findings: 'No signs of chemical contamination or heat damage.', nextDue: '01 Feb 2026', certificate: 'INS-FAL-0801' },
    ],
  },
  {
    id: 'Q008',
    name: 'Safety Goggles (Chemical)',
    batch: 'SG-2026-04',
    site: 'Site Beta',
    category: 'PPE',
    standard: 'EN 166',
    expiryDate: '15 Apr 2027',
    daysToExpiry: 260,
    nextInspection: '15 Oct 2026',
    daysToInspection: 78,
    status: 'ALL_OK',
    quantity: 15,
    unit: 'PCS',
    certNumber: 'CERT-SG-2026-04-H',
    inspectionHistory: [
      { date: '15 Apr 2026', inspector: 'Priya Sharma', result: 'PASS', findings: 'Lens clarity excellent. Seal gasket supple. No scratches.', nextDue: '15 Oct 2026', certificate: 'INS-SG-0415' },
    ],
  },
  {
    id: 'Q009',
    name: 'Fire Blanket (1.8m × 1.8m)',
    batch: 'FB-2026-01',
    site: 'Warehouse B',
    category: 'Fire Safety',
    standard: 'EN 1869',
    expiryDate: '10 Jan 2028',
    daysToExpiry: 530,
    nextInspection: '10 Jan 2027',
    daysToInspection: 165,
    status: 'ALL_OK',
    quantity: 3,
    unit: 'PCS',
    certNumber: 'CERT-FB-2026-01-I',
    inspectionHistory: [
      { date: '10 Jan 2026', inspector: 'Ravi Nair', result: 'PASS', findings: 'Packaging intact. No signs of moisture or contamination.', nextDue: '10 Jan 2027', certificate: 'INS-FB-0110' },
    ],
  },
  {
    id: 'Q010',
    name: 'Ear Defenders (SNR 35)',
    batch: 'ED-2026-03',
    site: 'Site Alpha',
    category: 'PPE',
    standard: 'EN 352-1',
    expiryDate: '20 Mar 2028',
    daysToExpiry: 600,
    nextInspection: '20 Sep 2026',
    daysToInspection: 53,
    status: 'ALL_OK',
    quantity: 10,
    unit: 'PCS',
    certNumber: 'CERT-ED-2026-03-J',
    inspectionHistory: [
      { date: '20 Mar 2026', inspector: 'Priya Sharma', result: 'PASS', findings: 'Cushion pads supple. Headband tension correct. Attenuation test passed.', nextDue: '20 Sep 2026', certificate: 'INS-ED-0320' },
    ],
  },
];

const STATUS_CONFIG: Record<ItemStatus, { label: string; bg: string; text: string; border: string; dot: string; sortOrder: number }> = {
  EXPIRED: { label: 'Expired', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500', sortOrder: 0 },
  EXPIRING_SOON: { label: 'Expiring Soon', bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500', sortOrder: 1 },
  INSPECTION_DUE: { label: 'Inspection Due', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500', sortOrder: 2 },
  ALL_OK: { label: 'All OK', bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-200', dot: 'bg-teal-500', sortOrder: 3 },
};

const RESULT_CONFIG: Record<InspectionRecord['result'], { label: string; bg: string; text: string }> = {
  PASS: { label: 'Pass', bg: 'bg-teal-100', text: 'text-teal-700' },
  FAIL: { label: 'Fail', bg: 'bg-red-100', text: 'text-red-700' },
  CONDITIONAL: { label: 'Conditional', bg: 'bg-amber-100', text: 'text-amber-700' },
};

const STATUS_FILTERS: Array<{ key: ItemStatus | 'ALL'; label: string }> = [
  { key: 'ALL', label: 'All Items' },
  { key: 'EXPIRED', label: 'Expired' },
  { key: 'EXPIRING_SOON', label: 'Expiring Soon' },
  { key: 'INSPECTION_DUE', label: 'Inspection Due' },
  { key: 'ALL_OK', label: 'All OK' },
];

interface Props {
  selectedSite: string;
}

export default function QHSEItemsList({ selectedSite }: Props) {
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ItemStatus | 'ALL'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = ALL_ITEMS
    .filter((item) => {
      const siteMatch = selectedSite === 'All Sites' || item.site === selectedSite;
      const statusMatch = statusFilter === 'ALL' || item.status === statusFilter;
      const searchMatch = !searchQuery || item.name.toLowerCase().includes(searchQuery.toLowerCase()) || item.batch.toLowerCase().includes(searchQuery.toLowerCase());
      return siteMatch && statusMatch && searchMatch;
    })
    .sort((a, b) => {
      const orderDiff = STATUS_CONFIG[a.status].sortOrder - STATUS_CONFIG[b.status].sortOrder;
      if (orderDiff !== 0) return orderDiff;
      return a.daysToExpiry - b.daysToExpiry;
    });

  const toggleItem = (id: string) => {
    setExpandedItem((prev) => (prev === id ? null : id));
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      {/* List header */}
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Icon name="ShieldExclamationIcon" size={16} className="text-gray-500" />
            <h3 className="text-sm font-700 text-gray-800">QHSE Items</h3>
            <span className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-600">{filtered.length} items</span>
          </div>
          {/* Search */}
          <div className="relative">
            <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search items or batch..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-[12px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-300 w-52"
            />
          </div>
        </div>

        {/* Status filter tabs */}
        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          {STATUS_FILTERS.map((f) => {
            const count = f.key === 'ALL'
              ? ALL_ITEMS.filter((i) => selectedSite === 'All Sites' || i.site === selectedSite).length
              : ALL_ITEMS.filter((i) => i.status === f.key && (selectedSite === 'All Sites' || i.site === selectedSite)).length;
            const isActive = statusFilter === f.key;
            const cfg = f.key !== 'ALL' ? STATUS_CONFIG[f.key] : null;
            return (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={[
                  'flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-600 border transition-all',
                  isActive
                    ? cfg ? `${cfg.bg} ${cfg.text} ${cfg.border}` : 'bg-gray-800 text-white border-gray-800' :'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100',
                ].join(' ')}
              >
                {cfg && <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />}
                {f.label}
                <span className={`text-[10px] font-700 ${isActive ? 'opacity-80' : 'text-gray-400'}`}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Items */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <Icon name="ShieldCheckIcon" size={36} className="mb-3 opacity-30" />
          <p className="text-sm font-600">No items match the current filter</p>
          <p className="text-[11px] mt-1">Try changing the site or status filter</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {filtered.map((item) => {
            const cfg = STATUS_CONFIG[item.status];
            const isExpanded = expandedItem === item.id;
            const isUrgent = item.status === 'EXPIRED' || item.status === 'EXPIRING_SOON';

            return (
              <div key={item.id} className={isUrgent && item.status === 'EXPIRED' ? 'bg-red-50/30' : ''}>
                {/* Item row */}
                <button
                  onClick={() => toggleItem(item.id)}
                  className="w-full text-left px-5 py-3.5 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {/* Status dot */}
                    <div className={`w-2 h-2 rounded-full shrink-0 ${cfg.dot} ${item.status === 'EXPIRED' ? 'animate-pulse' : ''}`} />

                    {/* Item info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[13px] font-600 text-gray-800">{item.name}</span>
                        <span className={`text-[9px] font-700 uppercase tracking-wide px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                          {cfg.label}
                        </span>
                        {item.status === 'EXPIRED' && (
                          <span className="text-[9px] font-700 text-red-600 bg-red-100 px-1.5 py-0.5 rounded">
                            {Math.abs(item.daysToExpiry)}d overdue
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        <span className="text-[10px] text-gray-500">Batch: <span className="font-600 text-gray-600">{item.batch}</span></span>
                        <span className="text-[10px] text-gray-400">·</span>
                        <span className="text-[10px] text-gray-500">{item.site}</span>
                        <span className="text-[10px] text-gray-400">·</span>
                        <span className="text-[10px] text-gray-500">{item.category}</span>
                        <span className="text-[10px] text-gray-400">·</span>
                        <span className="text-[10px] text-gray-500">{item.standard}</span>
                      </div>
                    </div>

                    {/* Expiry info */}
                    <div className="text-right shrink-0 hidden sm:block">
                      <p className="text-[10px] text-gray-400 mb-0.5">Expires</p>
                      <p className={`text-[12px] font-700 ${item.daysToExpiry < 0 ? 'text-red-600' : item.daysToExpiry <= 14 ? 'text-amber-600' : 'text-gray-700'}`}>
                        {item.expiryDate}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {item.daysToExpiry < 0
                          ? `${Math.abs(item.daysToExpiry)}d ago`
                          : `in ${item.daysToExpiry}d`}
                      </p>
                    </div>

                    {/* Inspection info */}
                    <div className="text-right shrink-0 hidden md:block">
                      <p className="text-[10px] text-gray-400 mb-0.5">Next Inspection</p>
                      <p className={`text-[12px] font-700 ${item.daysToInspection < 0 ? 'text-orange-600' : item.daysToInspection <= 7 ? 'text-orange-500' : 'text-gray-700'}`}>
                        {item.nextInspection}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {item.daysToInspection < 0
                          ? `${Math.abs(item.daysToInspection)}d overdue`
                          : `in ${item.daysToInspection}d`}
                      </p>
                    </div>

                    {/* Qty */}
                    <div className="text-right shrink-0 hidden lg:block">
                      <p className="text-[10px] text-gray-400 mb-0.5">Qty</p>
                      <p className="text-[13px] font-700 text-gray-700">{item.quantity} <span className="text-[10px] font-500 text-gray-400">{item.unit}</span></p>
                    </div>

                    {/* Expand toggle */}
                    <div className="shrink-0 ml-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${isExpanded ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-400'}`}>
                        <Icon name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={13} />
                      </div>
                    </div>
                  </div>
                </button>

                {/* Inline inspection history */}
                {isExpanded && (
                  <div className="px-5 pb-4 bg-gray-50/70 border-t border-gray-100">
                    <div className="pt-4 space-y-3">
                      {/* Item meta */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        {[
                          { label: 'Certificate No.', value: item.certNumber },
                          { label: 'Compliance Standard', value: item.standard },
                          { label: 'Quantity Remaining', value: `${item.quantity} ${item.unit}` },
                          { label: 'Site', value: item.site },
                        ].map((m) => (
                          <div key={m.label} className="bg-white border border-gray-200 rounded-lg px-3 py-2">
                            <p className="text-[9px] font-700 uppercase tracking-widest text-gray-400">{m.label}</p>
                            <p className="text-[12px] font-600 text-gray-700 mt-0.5">{m.value}</p>
                          </div>
                        ))}
                      </div>

                      {/* Inspection history header */}
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="ClipboardDocumentCheckIcon" size={14} className="text-teal-600" />
                        <h4 className="text-[12px] font-700 text-gray-700">Inspection History</h4>
                        <span className="text-[10px] bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-600">{item.inspectionHistory.length} records</span>
                      </div>

                      {item.inspectionHistory.length === 0 ? (
                        <div className="text-center py-6 text-gray-400">
                          <Icon name="ClipboardDocumentListIcon" size={24} className="mx-auto mb-2 opacity-40" />
                          <p className="text-[11px]">No inspection records yet</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {item.inspectionHistory.map((rec, idx) => {
                            const resCfg = RESULT_CONFIG[rec.result];
                            return (
                              <div key={idx} className="bg-white border border-gray-200 rounded-xl p-3.5">
                                <div className="flex items-start gap-3">
                                  {/* Timeline dot */}
                                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${resCfg.bg}`}>
                                    <Icon
                                      name={rec.result === 'PASS' ? 'CheckCircleIcon' : rec.result === 'FAIL' ? 'XCircleIcon' : 'ExclamationCircleIcon'}
                                      size={15}
                                      className={resCfg.text}
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-[12px] font-700 text-gray-800">{rec.date}</span>
                                      <span className={`text-[9px] font-700 uppercase tracking-wide px-2 py-0.5 rounded-full ${resCfg.bg} ${resCfg.text}`}>
                                        {resCfg.label}
                                      </span>
                                      {rec.certificate && (
                                        <span className="text-[9px] text-gray-400 font-500">Cert: {rec.certificate}</span>
                                      )}
                                    </div>
                                    <p className="text-[11px] text-gray-600 mt-1 leading-relaxed">{rec.findings}</p>
                                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                                      <div className="flex items-center gap-1">
                                        <Icon name="UserIcon" size={11} className="text-gray-400" />
                                        <span className="text-[10px] text-gray-500">{rec.inspector}</span>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Icon name="CalendarDaysIcon" size={11} className="text-gray-400" />
                                        <span className="text-[10px] text-gray-500">Next due: <span className="font-600 text-gray-600">{rec.nextDue}</span></span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-2 pt-2">
                        <button className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-[11px] font-600 rounded-lg transition-colors">
                          <Icon name="PlusIcon" size={12} />
                          Log Inspection
                        </button>
                        <button className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-[11px] font-600 rounded-lg transition-colors">
                          <Icon name="DocumentArrowDownIcon" size={12} />
                          Export History
                        </button>
                        <button className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-[11px] font-600 rounded-lg transition-colors">
                          <Icon name="PrinterIcon" size={12} />
                          Print Label
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
